import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/auth?error=google&detail=no-session-id", { replace: true });
      return;
    }
    const sessionId = match[1];
    // eslint-disable-next-line no-console
    console.log("[AuthCallback] Exchanging session_id (len=" + sessionId.length + ")");

    (async () => {
      try {
        const { data } = await api.post("/auth/google/callback", { session_id: sessionId });
        // eslint-disable-next-line no-console
        console.log("[AuthCallback] Session established for user:", data?.email);
        setUser(data);
        // Belt-and-suspenders: re-fetch /auth/me so the cookie is verified
        // by the server before we navigate to a protected route. This closes
        // the tiny race window where the cookie is set but not yet echoed by
        // the browser on the next request.
        try { await refresh?.(); } catch (_e) { /* non-fatal */ }
        // Clean hash and navigate to chat
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/chat", { replace: true, state: { user: data } });
      } catch (e) {
        const detail = formatApiError(e) || "unknown error";
        // eslint-disable-next-line no-console
        console.error("[AuthCallback] Google callback failed:", detail, e);
        navigate("/auth?error=google&detail=" + encodeURIComponent(detail.slice(0, 200)), { replace: true });
      }
    })();
  }, [navigate, setUser, refresh]);

  return (
    <div data-testid="auth-callback" className="h-screen flex items-center justify-center bg-background text-foreground">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Signing you in securely…
      </div>
    </div>
  );
}
